// Карточка лида: суть (статус и деньги) → история по времени → откуда пришёл → технические данные.
// Порядок подачи как везде: сначала вывод, детали ниже, сырые данные свёрнуты (DESIGN §4).
import { Button, Chip, EmptyState, IconButton, Skeleton, toast } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, MessageCircle, SearchX, Send, ShieldAlert, TriangleAlert } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ApiError, api } from '../api';
import {
  capFirst,
  FRAUD_LEVEL,
  LEAD_SOURCE,
  type LeadDetail,
  leadStatus,
  leadTimeline,
  money,
  PAYOUT_STATUS,
  phoneTel,
  seenAt,
  useLead,
} from '../business';
import { chatStore } from '../chat/store';
import { SidePanel } from '../shell/SidePanel';

export function LeadCard({ id, onClose }: { id: number | undefined; onClose: () => void }) {
  const q = useLead(id);
  const l = q.data;
  const ask = l
    ? () => {
        onClose();
        // Новый чат с контекстом лида: агент видит, о чём речь, без пересказа
        chatStore.setThread(null);
        chatStore.setContext({ kind: 'lead', id: String(l.id), label: askLabel(l) });
        chatStore.open();
      }
    : undefined;
  return (
    <SidePanel
      open={id !== undefined}
      label="Карточка лида"
      onClose={onClose}
      head={
        l ? (
          <>
            <b>{l.name ?? 'Без имени'}</b>
            <span className="mono">{l.phone ?? `Лид #${l.id}`}</span>
          </>
        ) : (
          <>
            <b>Лид</b>
            <span>{id !== undefined ? `#${id}` : ''}</span>
          </>
        )
      }
      footer={
        ask && (
          <Button block onClick={ask}>
            <MessageCircle />
            Спросить агента об этом лиде
          </Button>
        )
      }
    >
      {q.isPending && <CardSkeleton />}
      {q.isError &&
        (q.error instanceof ApiError && q.error.status === 404 ? (
          <EmptyState icon={<SearchX />} title="Лид не найден">
            Возможно, ссылка устарела. Закройте карточку и выберите лид в списке.
          </EmptyState>
        ) : (
          <EmptyState
            icon={<SearchX />}
            title="Не удалось открыть лид"
            action={<Button onClick={() => q.refetch()}>Повторить</Button>}
          >
            {(q.error as Error).message}
          </EmptyState>
        ))}
      {l && <LeadBody l={l} />}
    </SidePanel>
  );
}

function askLabel(l: LeadDetail): string {
  const parts = [`Лид #${l.id}`, l.name, leadStatus(l.status).label, l.offer.name].filter(Boolean);
  if (l.status === 'approved' && l.payout !== null) parts.push(money(l.payout, l.payoutCurrency));
  return parts.join(' · ').slice(0, 160);
}

function LeadBody({ l }: { l: LeadDetail }) {
  const st = leadStatus(l.status);
  const timeline = leadTimeline(l);
  const a = l.attribution;
  const utm = Object.entries(a.utm).filter(([, v]) => v);
  const hasAds = Boolean(a.campaignId || a.adsetId || a.adId);
  return (
    <>
      <section className="lead-sum">
        <div>
          <span>Статус</span>
          <Chip tone={st.tone}>{st.label}</Chip>
          {l.rejectReason && <small>{l.rejectReason}</small>}
        </div>
        <div>
          <span>Выплата</span>
          <b className="tnum">
            {l.payout !== null && l.status === 'approved' ? money(l.payout, l.payoutCurrency) : '—'}
          </b>
          <small>
            {l.status !== 'approved'
              ? 'будет после апрува'
              : l.payoutStatus === 'hold' && l.holdEndAt
                ? `холд до ${seenAt(l.holdEndAt)}`
                : l.payoutStatus
                  ? (PAYOUT_STATUS[l.payoutStatus] ?? l.payoutStatus)
                  : ''}
          </small>
        </div>
      </section>

      {/* Задержанный антифродом ждёт решения — это главное в карточке, сразу под сутью */}
      {l.landing?.fraud && l.delivery.status === 'held' && <FraudBlock l={l} />}
      {/* Не ушёл в buyo — это деньги: причина и «Отправить заново» тоже сразу под сутью */}
      {deliveryStuck(l) && <DeliveryBlock l={l} />}

      <dl className="facts">
        <Fact k="Телефон">
          <Phone phone={l.phone} />
        </Fact>
        <Fact k="Оффер">
          {l.offer.name ?? '—'}
          {l.offer.name && !l.offer.mine && <small>из каталога buyo</small>}
        </Fact>
        <Fact k="Поток">{l.offer.flowName ?? (l.offer.flowExtId ? `Поток ${l.offer.flowExtId}` : '—')}</Fact>
        <Fact k="Откуда">{LEAD_SOURCE[l.source]}</Fact>
      </dl>

      <section className="sp-sec">
        <h3>История</h3>
        <ol className="timeline">
          {timeline.map((t) => (
            <li key={t.key} data-tone={t.tone} data-future={t.at === null || undefined}>
              <span className="tl-dot" aria-hidden="true" />
              <div>
                <b>{t.title}</b>
                {t.detail && <span>{t.detail}</span>}
                <small>
                  {t.at && <time dateTime={t.at}>{seenAt(t.at)}</time>}
                  {t.at && t.sub && ' · '}
                  {t.sub}
                </small>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {l.landing?.fraud && l.landing.fraud.reasons.length > 0 && l.delivery.status !== 'held' && (
        <FraudBlock l={l} />
      )}

      <section className="sp-sec">
        <h3>Реклама</h3>
        {hasAds || utm.length > 0 ? (
          <dl className="facts">
            {a.campaignId && (
              <Fact k="Кампания" mono>
                {a.campaignId}
              </Fact>
            )}
            {a.adsetId && (
              <Fact k="Адсет" mono>
                {a.adsetId}
              </Fact>
            )}
            {a.adId && (
              <Fact k="Объявление" mono>
                {a.adId}
              </Fact>
            )}
            {a.placement && <Fact k="Плейсмент">{a.placement}</Fact>}
            {utm.map(([k, v]) => (
              <Fact key={k} k={`utm_${k}`} mono>
                {v}
              </Fact>
            ))}
          </dl>
        ) : (
          <p className="muted sp-note">
            {l.source === 'network'
              ? 'Лид пришёл не через платформу, меток рекламы у него нет — связать с объявлением нельзя.'
              : 'У лида нет меток рекламы — связать с объявлением нельзя.'}
          </p>
        )}
      </section>

      {/* Внизу карточки, над закреплённой кнопкой: раскрыли — прокручиваем, чтобы содержимое не пряталось под ней */}
      <details
        className="sp-raw"
        onToggle={(e) => {
          if (e.currentTarget.open) e.currentTarget.scrollIntoView({ block: 'nearest' });
        }}
      >
        <summary>Технические данные</summary>
        <dl className="facts">
          <Fact k="Лид в платформе" mono>
            #{l.id}
          </Fact>
          {l.networkIds.leadId && (
            <Fact k="id в buyo" mono>
              {l.networkIds.leadId}
            </Fact>
          )}
          {l.networkIds.lid && (
            <Fact k="lid в buyo" mono>
              {l.networkIds.lid}
            </Fact>
          )}
          {l.source === 'platform' && (
            <Fact k="Отправка в buyo">
              {l.delivery.status === 'sent'
                ? `отправлен${l.delivery.sentAt ? `, ${seenAt(l.delivery.sentAt)}` : ''}`
                : l.delivery.status === 'error'
                  ? `ошибка после ${l.delivery.attempts} ${attemptsWord(l.delivery.attempts)}: ${l.delivery.error ?? ''}`
                  : l.delivery.status === 'pending'
                    ? 'в очереди'
                    : l.delivery.status === 'held'
                      ? 'задержан антифродом'
                      : l.delivery.status === 'dropped'
                        ? 'не отправлен — решение владельца'
                        : l.delivery.status}
            </Fact>
          )}
          {l.networkIds.syncedAt && <Fact k="Сверен с buyo">{seenAt(l.networkIds.syncedAt)}</Fact>}
        </dl>
        {l.raw !== null && l.raw !== undefined && (
          <pre className="mono">{JSON.stringify(l.raw, null, 2)}</pre>
        )}
      </details>
    </>
  );
}

/**
 * Лид с нашего лендинга не ушёл в buyo: ошибка отправки или ждёт дольше, чем платформа досылает сама.
 * Обычная очередь (ждёт первой отправки, повтор по расписанию ещё не пришёл) — без блока.
 */
const deliveryStuck = (l: LeadDetail) =>
  l.source === 'platform' &&
  Boolean(l.delivery.canResend) &&
  (l.delivery.status === 'error' || !l.delivery.nextAt);

/** Почему лид не ушёл в сеть, повторит ли платформа сама, и «Отправить заново». */
function DeliveryBlock({ l }: { l: LeadDetail }) {
  const d = l.delivery;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await api(`/api/leads/${l.id}/resend`, {});
      toast('Лид ушёл в очередь на отправку в buyo');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['lead', l.id] }),
        qc.invalidateQueries({ queryKey: ['leads'] }),
      ]);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const reason = d.error ? `: ${d.error}` : '';
  const text =
    d.status === 'pending'
      ? 'Лид ждёт отправки больше суток — сама платформа его уже не отправит. Если клиент ещё ждёт звонка, отправьте.'
      : d.permanent
        ? `Сеть отказала${reason}. Сама платформа больше не пробует — исправьте причину (например, поток оффера) и отправьте заново.`
        : d.nextAt
          ? `Не удалось отправить${reason}. Попыток: ${d.attempts} — платформа попробует снова ${seenAt(d.nextAt)} и будет пробовать сутки с заявки; можно не ждать.`
          : `Не ушёл за сутки, попыток: ${d.attempts}${reason}. Сама платформа больше не пробует.`;
  return (
    <section className="sp-sec">
      <h3>Отправка в buyo</h3>
      <div className="fraud-held">
        <p>
          <TriangleAlert aria-hidden="true" />
          {text}
        </p>
        <div className="fraud-acts">
          <Button variant={d.nextAt ? undefined : 'primary'} disabled={busy} onClick={() => void resend()}>
            <Send />
            Отправить заново
          </Button>
        </div>
      </div>
    </section>
  );
}

/** Антифрод: риск, признаки с баллами; задержанный — отправить в сеть или оставить. */
function FraudBlock({ l }: { l: LeadDetail }) {
  const f = l.landing?.fraud;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (!f) return null;
  const lv = FRAUD_LEVEL[f.level];
  const act = async (a: 'release' | 'drop') => {
    setBusy(true);
    try {
      await api(`/api/leads/${l.id}/${a}`, {});
      toast(
        a === 'release' ? 'Лид ушёл в очередь на отправку в сеть и в Meta' : 'Лид останется неотправленным',
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['lead', l.id] }),
        qc.invalidateQueries({ queryKey: ['leads'] }),
      ]);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="sp-sec fraud">
      <h3>Антифрод</h3>
      <div className="fraud-head">
        <Chip tone={lv.tone} dot>
          {capFirst(lv.label)}
        </Chip>
        <span className="tnum">{f.score} из 100</span>
      </div>
      {f.reasons.length > 0 && (
        <ul className="fraud-reasons">
          {f.reasons.map((r) => (
            <li key={r.id}>
              <span>{r.label}</span>
              <b className="tnum">+{r.points}</b>
            </li>
          ))}
        </ul>
      )}
      {l.delivery.status === 'held' && (
        <div className="fraud-held">
          <p>
            <ShieldAlert aria-hidden="true" />
            {l.landing?.fraudFlags.includes('ip_flood')
              ? 'С этого IP больше 5 заявок за 10 минут — лид задержан и не ушёл ни в сеть, ни в Meta. У мобильных операторов бывает общий IP: если это живой клиент, отправьте его.'
              : 'Риск выше вашего порога — лид не ушёл ни в сеть, ни в Meta. Если это живой клиент, отправьте его.'}
          </p>
          <div className="fraud-acts">
            <Button variant="primary" disabled={busy} onClick={() => act('release')}>
              <Send />
              Отправить в сеть
            </Button>
            <Button disabled={busy} onClick={() => act('drop')}>
              Не отправлять
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Полный номер — звонок по нажатию и «скопировать»; скрытый сетью — как есть, с пояснением. */
function Phone({ phone }: { phone: string | null }) {
  const tel = phoneTel(phone);
  if (!phone) return <>—</>;
  if (!tel)
    return (
      <>
        <span className="mono">{phone}</span>
        <small>сеть показывает только начало номера</small>
      </>
    );
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tel);
      toast('Номер скопирован');
    } catch {
      toast('Не удалось скопировать — выделите номер вручную');
    }
  };
  return (
    <span className="lead-phone">
      <a className="mono" href={`tel:${tel}`}>
        {phone}
      </a>
      <IconButton label="Скопировать номер" onClick={() => void copy()}>
        <Copy aria-hidden />
      </IconButton>
    </span>
  );
}

const attemptsWord = (n: number) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'попытки';
  return 'попыток';
};

function Fact({ k, mono, children }: { k: string; mono?: boolean; children: ReactNode }) {
  return (
    <div>
      <dt>{k}</dt>
      <dd className={mono ? 'mono' : undefined}>{children}</dd>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton h={64} />
      <Skeleton w="80%" />
      <Skeleton w="60%" />
      <Skeleton w="70%" />
      <Skeleton h={120} />
    </div>
  );
}
